import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Eye, EyeOff, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const Field = ({ label, value, onChange, placeholder, type = 'text', masked, onToggleMask, hint }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    <div className="relative">
      <input
        type={masked ? 'password' : type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-10 text-sm outline-none focus:border-indigo-400 font-mono"
      />
      {onToggleMask && (
        <button type="button" onClick={onToggleMask}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
          {masked ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      )}
    </div>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default function PaymentSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [masked, setMasked] = useState({ ps_secret: true, flw_secret: true });

  const [form, setForm] = useState({
    paystack_secret_key: '',
    paystack_public_key: '',
    flutterwave_secret_key: '',
    flutterwave_public_key: '',
    flutterwave_secret_hash: '',
    active_gateways: ['paystack', 'flutterwave'],
    mode: 'test',
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/master/gateway-settings');
      const data = res.data;
      setForm(prev => ({
        ...prev,
        paystack_public_key: data.paystack_public_key || '',
        flutterwave_public_key: data.flutterwave_public_key || '',
        flutterwave_secret_hash: data.flutterwave_secret_hash || '',
        active_gateways: data.active_gateways || ['paystack', 'flutterwave'],
        mode: data.mode || 'test',
      }));
    } catch {
      toast.error('Failed to load gateway settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleGateway = (gw) => {
    setForm(prev => ({
      ...prev,
      active_gateways: prev.active_gateways.includes(gw)
        ? prev.active_gateways.filter(g => g !== gw)
        : [...prev.active_gateways, gw],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send empty secret key fields (they'd overwrite the stored values)
      if (!payload.paystack_secret_key || payload.paystack_secret_key.includes('*')) delete payload.paystack_secret_key;
      if (!payload.flutterwave_secret_key || payload.flutterwave_secret_key.includes('*')) delete payload.flutterwave_secret_key;

      await api.put('/master/gateway-settings', payload);
      toast.success('Payment gateway settings saved');
      setForm(prev => ({ ...prev, paystack_secret_key: '', flutterwave_secret_key: '' }));
    } catch (err) {
      toast.error(err?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
    </div>
  );

  const isConfigured = (prefix) => {
    if (prefix === 'paystack') return form.paystack_public_key && !form.paystack_public_key.includes('placeholder');
    return form.flutterwave_public_key && !form.flutterwave_public_key.includes('placeholder');
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Payment Gateway Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Configure Paystack and Flutterwave API keys for school subscription billing</p>
      </div>

      {/* Mode Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-4">Mode</h2>
        <div className="flex gap-3">
          {['test', 'live'].map(m => (
            <button key={m} onClick={() => setForm(p => ({ ...p, mode: m }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors capitalize
                ${form.mode === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`mode-${m}-btn`}>
              {m === 'test' ? 'Test Mode' : 'Live Mode'}
            </button>
          ))}
        </div>
        {form.mode === 'test' && (
          <div className="flex items-start gap-2 mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Test mode: use test API keys. No real payments will be processed.
          </div>
        )}
        {form.mode === 'live' && (
          <div className="flex items-start gap-2 mt-3 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Live mode: real payments will be processed. Ensure you use live API keys.
          </div>
        )}
      </div>

      {/* Active Gateways */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-4">Active Payment Gateways</h2>
        <div className="flex gap-3">
          {['paystack', 'flutterwave'].map(gw => (
            <label key={gw} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors flex-1
              ${form.active_gateways.includes(gw) ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-slate-50'}`}>
              <input type="checkbox" checked={form.active_gateways.includes(gw)} onChange={() => toggleGateway(gw)} className="accent-indigo-600 w-4 h-4" />
              <div>
                <p className="text-sm font-semibold text-slate-800 capitalize">{gw}</p>
                <p className="text-xs text-slate-500">{isConfigured(gw) ? '✓ Configured' : 'Not configured'}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Paystack */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="paystack-settings">
        <div className="flex items-center gap-2.5 mb-1">
          <CreditCard className="w-5 h-5 text-[#00C3F7]" />
          <h2 className="text-sm font-semibold text-slate-800">Paystack</h2>
          {isConfigured('paystack') && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Configured</span>}
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          Get your keys from <a href="https://dashboard.paystack.com" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">dashboard.paystack.com</a> → Settings → API Keys
        </p>
        <Field label="Secret Key (Backend)" value={form.paystack_secret_key} onChange={v => setForm(p => ({ ...p, paystack_secret_key: v }))}
          placeholder="sk_test_xxxxxxxxxxxx or sk_live_xxxxxxxxxxxx"
          masked={masked.ps_secret} onToggleMask={() => setMasked(p => ({ ...p, ps_secret: !p.ps_secret }))}
          hint="Leave blank to keep existing key. Only enter when updating." />
        <Field label="Public Key (Frontend)" value={form.paystack_public_key} onChange={v => setForm(p => ({ ...p, paystack_public_key: v }))}
          placeholder="pk_test_xxxxxxxxxxxx or pk_live_xxxxxxxxxxxx"
          hint="Used on the frontend checkout." />
      </div>

      {/* Flutterwave */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="flutterwave-settings">
        <div className="flex items-center gap-2.5 mb-1">
          <CreditCard className="w-5 h-5 text-[#F5A623]" />
          <h2 className="text-sm font-semibold text-slate-800">Flutterwave</h2>
          {isConfigured('flutterwave') && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Configured</span>}
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          Get your keys from <a href="https://dashboard.flutterwave.com" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">dashboard.flutterwave.com</a> → Settings → API Keys
        </p>
        <Field label="Secret Key (Backend)" value={form.flutterwave_secret_key} onChange={v => setForm(p => ({ ...p, flutterwave_secret_key: v }))}
          placeholder="FLWSECK_TEST-xxxxxxxx or FLWSECK-xxxxxxxx"
          masked={masked.flw_secret} onToggleMask={() => setMasked(p => ({ ...p, flw_secret: !p.flw_secret }))}
          hint="Leave blank to keep existing key. Only enter when updating." />
        <Field label="Public Key (Frontend)" value={form.flutterwave_public_key} onChange={v => setForm(p => ({ ...p, flutterwave_public_key: v }))}
          placeholder="FLWPUBK_TEST-xxxxxxxx or FLWPUBK-xxxxxxxx" />
        <Field label="Webhook Secret Hash" value={form.flutterwave_secret_hash} onChange={v => setForm(p => ({ ...p, flutterwave_secret_hash: v }))}
          placeholder="Your webhook secret hash from Flutterwave dashboard"
          hint="Set this in Flutterwave Dashboard → Settings → Webhooks to verify webhook authenticity." />
      </div>

      {/* Webhook URLs */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Webhook URLs</h2>
        <p className="text-xs text-slate-500">Configure these in your payment gateway dashboards to receive payment notifications:</p>
        <div className="space-y-2">
          {['paystack', 'flutterwave'].map(gw => (
            <div key={gw} className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-xs font-medium text-slate-600 capitalize">{gw}</span>
              <code className="text-xs text-indigo-600 font-mono">{window.location.origin}/api/subscriptions/{gw}/webhook</code>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
          data-testid="save-gateway-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}
